import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/utils/cn'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'

/**
 * Input Group Component
 *
 * Groups an input with addons (prefix/suffix) or buttons.
 *
 * @see https://ui.shadcn.com/docs/components/base/input-group
 *
 * @example
 * ```tsx
 * <InputGroup>
 *   <InputGroupAddon align="inline-start">$</InputGroupAddon>
 *   <InputGroupInput placeholder="0.00" />
 *   <InputGroupButton>
 *     <Button type="button">Submit</Button>
 *   </InputGroupButton>
 * </InputGroup>
 * ```
 *
 * @remarks
 * Key Props:
 * - InputGroupAddon: `align` ('inline-start' | 'inline-end' | 'block-start' | 'block-end')
 * - InputGroupInput / InputGroupButton: see docs
 */

const inputGroupVariants = cva(
  'border-border has-[[data-slot=input-group-control]:focus-visible]:border-ring has-[[data-slot=input-group-control]:focus-visible]:ring-ring/50 has-[[data-slot][aria-invalid=true]]:ring-destructive/20 has-[[data-slot][aria-invalid=true]]:border-destructive dark:has-[[data-slot][aria-invalid=true]]:ring-destructive/40 rounded-md border shadow-xs transition-[color,box-shadow] has-[[data-slot=input-group-control]:focus-visible]:ring-[3px] has-[[data-slot][aria-invalid=true]]:ring-[3px] has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>[data-align=block-end]]:[&>input]:pt-3 has-[>[data-align=block-start]]:[&>input]:pb-3 has-[>[data-align=inline-end]]:[&>input]:pr-2 has-[>[data-align=inline-start]]:[&>input]:pl-2 [[data-slot=combobox-content]_&]:focus-within:border-inherit [[data-slot=combobox-content]_&]:focus-within:ring-0 group/input-group relative flex w-full min-w-0 items-center outline-none has-[>textarea]:h-auto',
  {
    variants: {
      inputSize: {
        // Pair with Button so a field and a button line up on one row: `sm` with Button's `sm` (h-8),
        // `default` with `default`/`action`/`submit` (all h-9), `lg` with `lg` (h-10). Each tier also
        // pins the nested control to the same height — which is exactly how `default` behaves
        // implicitly (h-9 group + the Input's own h-9 default). NB `h-full` does NOT work here:
        // against an indefinite flex cross-size it collapses to the 33px content height.
        sm: 'h-8 [&_[data-slot=input-group-control]]:h-8',
        default: 'h-9',
        lg: 'h-10 [&_[data-slot=input-group-control]]:h-10',
        hero: 'h-[66px] min-h-[66px] rounded-[calc(var(--radius)-2px)] px-4 [&_[data-slot=input-group-addon][data-align=inline-end]]:pr-0 [&_[data-slot=input-group-control]]:px-0 [&_[data-slot=input-group-control]]:shadow-none [&_[data-slot=input-group-control]]:focus-visible:ring-0',
        // hero without the fixed height, so wrapping content (chips, multi-select) grows the box.
        heroWrap:
          'min-h-[66px] flex-wrap gap-1 rounded-[calc(var(--radius)-2px)] px-4 py-3 [&_[data-slot=input-group-addon][data-align=inline-end]]:pr-0 [&_[data-slot=input-group-control]]:px-0 [&_[data-slot=input-group-control]]:shadow-none [&_[data-slot=input-group-control]]:focus-visible:ring-0',
      },
      variant: {
        default: 'bg-input',
        surface: 'bg-card shadow-none dark:bg-card',
        // Search chrome: filled, no resting border. Keeps a 1px *transparent* border so the base
        // `has-[…focus-visible]:border-ring` rule still paints and the box never shifts geometry on
        // focus, and pairs the base `shadow-xs` with a hover ring as the affordance that replaces it.
        search: 'bg-input border-transparent hover:ring-1 hover:ring-ring',
        // Unfilled field: reads via the base `border-border` hairline + `shadow-xs`, like
        // `Button variant="outline"`. Pair the two when they share a row.
        outline: 'bg-transparent',
      },
    },
    defaultVariants: {
      inputSize: 'default',
      variant: 'default',
    },
  },
)

function InputGroup({
  className,
  inputSize,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof inputGroupVariants>) {
  return (
    <div
      data-slot="input-group"
      data-input-size={inputSize ?? 'default'}
      data-variant={variant ?? 'default'}
      role="group"
      className={cn(inputGroupVariants({ inputSize, variant }), className)}
      {...props}
    />
  )
}

const inputGroupAddonVariants = cva(
  "text-muted-foreground h-auto gap-2 py-1.5 text-sm font-medium group-data-[disabled=true]/input-group:opacity-50 [&>kbd]:rounded-[calc(var(--radius)-5px)] [&>svg:not([class*='size-'])]:size-4 flex cursor-text items-center justify-center select-none",
  {
    variants: {
      align: {
        'inline-start': 'pl-3 has-[>button]:ml-[-0.25rem] has-[>kbd]:ml-[-0.15rem] order-first',
        'inline-end': 'pr-3 has-[>button]:mr-[-0.25rem] has-[>kbd]:mr-[-0.15rem] order-last',
        'block-start':
          'px-2.5 pt-2 group-has-[>input]/input-group:pt-2 [.border-b]:pb-2 order-first w-full justify-start',
        'block-end': 'px-2.5 pb-2 group-has-[>input]/input-group:pb-2 [.border-t]:pt-2 order-last w-full justify-start',
      },
    },
    defaultVariants: {
      align: 'inline-start',
    },
  },
)

function InputGroupAddon({
  className,
  align = 'inline-start',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof inputGroupAddonVariants>) {
  return (
    <div
      role="group"
      data-slot="input-group-addon"
      data-align={align}
      className={cn(inputGroupAddonVariants({ align }), className)}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) {
          return
        }
        e.currentTarget.parentElement?.querySelector('input')?.focus()
      }}
      {...props}
    />
  )
}

const inputGroupButtonVariants = cva('gap-2 text-sm shadow-none flex items-center', {
  variants: {
    size: {
      xs: "h-6 gap-1 rounded-[calc(var(--radius)-5px)] px-1.5 [&>svg:not([class*='size-'])]:size-3.5",
      sm: '',
      'icon-xs': 'size-6 rounded-[calc(var(--radius)-5px)] p-0 has-[>svg]:p-0',
      'icon-sm': 'size-8 p-0 has-[>svg]:p-0',
    },
  },
  defaultVariants: {
    size: 'xs',
  },
})

function InputGroupButton({
  className,
  type = 'button',
  variant = 'ghost',
  size = 'xs',
  ...props
}: Omit<React.ComponentProps<typeof Button>, 'size' | 'type'> &
  VariantProps<typeof inputGroupButtonVariants> & {
    type?: 'button' | 'submit' | 'reset'
  }) {
  return (
    <Button
      type={type}
      data-size={size}
      variant={variant}
      className={cn(inputGroupButtonVariants({ size }), className)}
      {...props}
    />
  )
}

function InputGroupText({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      className={cn(
        "text-muted-foreground gap-2 text-sm [&_svg:not([class*='size-'])]:size-4 flex items-center [&_svg]:pointer-events-none",
        className,
      )}
      {...props}
    />
  )
}

function InputGroupInput({ className, ...props }: React.ComponentProps<'input'>) {
  return (
    <Input
      data-slot="input-group-control"
      className={cn(
        // eslint-disable-next-line no-restricted-syntax -- primitive internal: strips the Input's own border/bg/radius/ring so it blends seamlessly into the InputGroup shell
        'rounded-none border-0 bg-transparent shadow-none ring-0 focus-visible:ring-0 aria-invalid:ring-0 dark:bg-transparent flex-1',
        className,
      )}
      {...props}
    />
  )
}

function InputGroupTextarea({ className, ...props }: React.ComponentProps<'textarea'>) {
  return (
    <Textarea
      data-slot="input-group-control"
      className={cn(
        'rounded-none border-0 bg-transparent py-2 shadow-none ring-0 focus-visible:ring-0 aria-invalid:ring-0 dark:bg-transparent flex-1 resize-none',
        className,
      )}
      {...props}
    />
  )
}

export { InputGroup, InputGroupAddon, InputGroupButton, InputGroupText, InputGroupInput, InputGroupTextarea }
